import {
  cloneElement,
  type FormEvent,
  type ReactElement,
  type ReactNode,
  useEffect,
  useState,
  type JSX,
} from "react";

import { cn } from "@repo/cn";
import {
  type CheckboxV2Props,
  CpsCheckboxGroup,
  CpsCheckboxV2,
  CpsRadioButtonGroup,
  CpsRadioButtonV2,
  type RadioButtonV2Props,
} from "corpus";

import {
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerRoot,
  DrawerTitle,
} from "@/components/ui/drawer/drawer";
import { SearchInput } from "@/components/ui/input/search-input";
import { normalizeToHTMLAttribute } from "@/lib/string";

export interface SelectDrawerItemProps<T extends string | number> {
  value: T;
  label: string;
  description?: string;
  badge?: ReactNode;
  disabled?: boolean;
}

interface SelectDrawerProps {
  children: ReactNode;
  open: boolean;
  setOpen: (open: boolean) => void;
  onClose?: () => void;
}

export const SelectDrawer = ({
  open,
  setOpen,
  onClose,
  children,
}: SelectDrawerProps): JSX.Element => {
  return (
    <DrawerRoot open={open} setOpen={setOpen} onClose={onClose}>
      <DrawerContent>{children}</DrawerContent>
    </DrawerRoot>
  );
};

interface SelectDrawerTitleProps {
  children: ReactNode;
}

export const SelectDrawerTitle = ({ children }: SelectDrawerTitleProps): JSX.Element => (
  <DrawerHeader>
    <DrawerTitle>{children}</DrawerTitle>
  </DrawerHeader>
);

interface SelectDrawerBodyProps<T extends string | number> {
  className?: string;
  children: ReactNode;
  drawerSelectedItems: SelectDrawerItemProps<T>[] | undefined;
}

const SelectDrawerBody = <T extends string | number>({
  className,
  children,
}: SelectDrawerBodyProps<T>): JSX.Element => {
  return (
    <DrawerBody>
      <div
        className={cn(
          "text-md font-normal text-neutral-500 max-w-md w-full mx-auto flex flex-col overflow-auto gap-y-1",
          className,
        )}
      >
        <ul className="grid grid-cols-1 [&>*+*]:border-t gap-[4px]">{children}</ul>
      </div>
    </DrawerBody>
  );
};

interface SelectDrawerBodySingleListProps<T extends string | number> {
  children: ReactElement<RadioButtonV2Props>[];
  initialSelectedItem: SelectDrawerItemProps<T> | undefined;
  setSelectedItem: (newValue: SelectDrawerItemProps<T> | undefined) => void;
}

export const SelectDrawerBodySingleList = <T extends string | number>({
  children,
  initialSelectedItem,
  setSelectedItem,
}: SelectDrawerBodySingleListProps<T>): JSX.Element => {
  const [drawerSelectedItem, setDrawerSelectedItem] = useState<
    SelectDrawerItemProps<T> | undefined
  >(initialSelectedItem);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  useEffect(() => {
    if (!initialSelectedItem) return;

    setSelectedItem(initialSelectedItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Carregamento inicial do componente para atualizar o valor já checado
  }, []);

  const handleChange = (value: string | FormEvent<HTMLDivElement>): void => {
    if (!children.length || !value) return;
    const item = children.find((x) => x.props.value.toString() === value);

    if (!item) return;

    setDrawerSelectedItem({
      label: item.props.label,
      value: item.props.value as T,
    });
    setSelectedItem({ label: item.props.label, value: item.props.value as T });
  };

  return (
    <SelectDrawerBody
      drawerSelectedItems={drawerSelectedItem ? [drawerSelectedItem] : undefined}
    >
      <CpsRadioButtonGroup
        defaultValue={initialSelectedItem?.value.toString()}
        onChange={handleChange}
      >
        {children}
      </CpsRadioButtonGroup>
    </SelectDrawerBody>
  );
};

interface SelectDrawerBodyListRadioProps<T extends string | number> {
  children: string;
  value: T;
  description?: string;
  disabled?: boolean;
}

export const SelectDrawerBodyListRadio = <T extends string | number>({
  children,
  value,
  description,
  disabled = false,
}: SelectDrawerBodyListRadioProps<T>): ReactElement => {
  return (
    <CpsRadioButtonV2
      id={normalizeToHTMLAttribute(`radio-${value}`)}
      label={children}
      description={description}
      value={value}
      disabled={disabled}
    />
  );
};

interface SelectDrawerBodyMultipleListProps<T extends string | number> {
  children: ReactElement<CheckboxV2Props>[];
  selectedItems: SelectDrawerItemProps<T>[] | undefined;
  setSelectedItems: (newValue: SelectDrawerItemProps<T>[] | undefined) => void;
  /**
   * Rótulo da linha "selecionar todos", acima da lista. Sem ele, não há linha. A linha aparece
   * marcada quando **todos** os itens estão marcados (inclusive um a um) e desmarcá-la limpa a
   * seleção inteira. Ela age sobre todos os `children`, escondidos por busca inclusive — quem filtra
   * deve omitir o rótulo enquanto o filtro estiver ativo.
   */
  selectAllLabel?: string;
}

/**
 * O que a linha "selecionar todos" impõe às demais. O grupo do Corpus e o checkbox do Radix só leem
 * `defaultChecked` na montagem, então cada ação da linha remonta o grupo (`version` vira `key`) com
 * todos os itens já no estado pedido. A remontagem é só visual: a seleção é gravada direto pela linha.
 */
interface BulkCheck {
  checked: boolean;
  version: number;
}

export const SelectDrawerBodyMultipleList = <T extends string | number>({
  children,
  selectedItems,
  setSelectedItems,
  selectAllLabel,
}: SelectDrawerBodyMultipleListProps<T>): JSX.Element => {
  const [drawerSelectedItems, setDrawerSelectedItems] = useState<
    SelectDrawerItemProps<T>[] | undefined
  >(selectedItems);
  const [bulkCheck, setBulkCheck] = useState<BulkCheck | null>(null);

  const allSelected =
    children.length > 0 && (drawerSelectedItems?.length ?? 0) === children.length;

  // Grava a seleção aqui em vez de depender do `onChange` que o grupo do Corpus dispara ao remontar.
  const handleSelectAll = (checked: boolean): void => {
    const items = checked
      ? (children.map((x) => ({
          label: x.props.children,
          value: x.props.value,
        })) as SelectDrawerItemProps<T>[])
      : [];

    setSelectedItems(items);
    setDrawerSelectedItems(items);
    setBulkCheck((previous) => ({ checked, version: (previous?.version ?? 0) + 1 }));
  };

  // Depois de uma ação da linha, o estado inicial de cada item é o que ela pediu, não o que veio de
  // fora; a remontagem pelo `key` é o que faz o grupo reler esse valor.
  const rows = bulkCheck
    ? children.map((child) => cloneElement(child, { checked: bulkCheck.checked }))
    : children;

  const handleChange = (value: string[]): void => {
    if (!children.length) return;
    const items = children
      .map((x) => {
        if (value.includes(x.props.value.toString())) {
          return { label: x.props.children, value: x.props.value };
        }

        return undefined;
      })
      .filter((item) => item !== undefined) as SelectDrawerItemProps<T>[];

    setSelectedItems(items);
    setDrawerSelectedItems(items);
  };

  return (
    <SelectDrawerBody drawerSelectedItems={drawerSelectedItems}>
      {selectAllLabel ? (
        // Fora do grupo de propósito: ela não é um valor da lista. Controlada, para refletir uma
        // seleção completa feita item a item.
        <CpsCheckboxV2
          id={normalizeToHTMLAttribute("checkbox-select-all")}
          color="primary"
          value="select-all"
          label={selectAllLabel}
          checked={allSelected}
          onCheckedChange={(checked) => handleSelectAll(checked === true)}
        />
      ) : null}
      <CpsCheckboxGroup
        key={bulkCheck?.version ?? 0}
        name="select-drawer-checkbox-group"
        onChange={handleChange}
      >
        {rows}
      </CpsCheckboxGroup>
    </SelectDrawerBody>
  );
};

interface SelectDrawerBodyListCheckboxProps<T extends string | number> {
  children: string;
  value: T;
  description?: string;
  badge?: ReactNode;
  checked?: boolean;
  className?: string;
}

export const SelectDrawerBodyListCheckbox = <T extends string | number>({
  children,
  value,
  description,
  badge,
  checked,
  className,
}: SelectDrawerBodyListCheckboxProps<T>): JSX.Element => {
  return (
    <CpsCheckboxV2
      className={className}
      color="primary"
      id={normalizeToHTMLAttribute(`checkbox-${children}-${value}`)}
      defaultChecked={checked}
      value={String(value)}
      label={
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <span>{children}</span>
            {badge}
          </div>
          {description ? (
            <p className="font-normal text-neutral-500 text-sm">{description}</p>
          ) : null}
        </div>
      }
    />
  );
};

interface SelectDrawerFooterProps {
  children: ReactNode;
}

export const SelectDrawerFooter = ({
  children,
}: SelectDrawerFooterProps): JSX.Element => {
  return <DrawerFooter>{children}</DrawerFooter>;
};

interface SelectDrawerSearchProps<T extends string | number> {
  items: SelectDrawerItemProps<T>[];
  onSearch: (items: SelectDrawerItemProps<T>[]) => void;
  placeholder?: string;
  onChange?: (value: string) => void;
}

export const SelectDrawerSearch = <T extends string | number>({
  items,
  onSearch,
  placeholder = "Buscar",
  onChange,
}: SelectDrawerSearchProps<T>): JSX.Element => {
  useEffect(() => {
    onSearch(items);
  }, [items, onSearch]);

  return (
    <SearchInput
      items={items}
      onSearch={onSearch}
      searchKeys={["label", "value"]}
      placeholder={placeholder}
      onChange={onChange}
    />
  );
};
